import { ElementType, useState } from 'react'
import * as S from './index.style'
import { parseISO } from 'date-fns'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import { InputBaseComponentProps } from '@material-ui/core/InputBase'
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker'
import Grid from 'components/Grid'
import { DialogHeader } from 'components/DialogHeader'
import { MascaraInteiro } from 'components/Mascara/Inteiro'
import { MascaraDecimal } from 'components/Mascara/Decimal'
import { DadosDatas } from 'models/Viabilidade/Datas'
import { DadosParcela, dadosParcelaValorDefault, ValidacaoDadosParcela } from 'models/Viabilidade/Parcela'
import { CamposParcelaEnum } from 'models/Viabilidade/Enum/CamposParcelaEnum'
import { CondicionantesEnum } from 'models/Viabilidade/Enum/CondicionantesEnum'
import { IndicesCorrecaoEnum } from 'models/Viabilidade/Enum/IndicesCorrecaoEnum'
import { Event } from '@styled-icons/material-rounded'
import theme from 'styles/theme'
import { campoOpcional } from 'helpers/Constantes/Mensagens'

export type NovaParcelaProps = {
  dadosParcela: DadosParcela
  indiceEdicao: number
  dadosDatas: DadosDatas
  adicionarParcelaHandler: (novoValor: DadosParcela) => void
  editarParcelaHandler: (novoValor: DadosParcela, indiceEdicao: number) => void
  closeDialogParcelaHandler: () => void
}

const NovaParcela: React.FC<NovaParcelaProps> = ({
  dadosParcela,
  indiceEdicao,
  dadosDatas,
  adicionarParcelaHandler,
  editarParcelaHandler,
  closeDialogParcelaHandler
}) => {
  const [valores, setValores] = useState(dadosParcela)
  const [mensagensErro, setMensagensErro] = useState(new ValidacaoDadosParcela())
  const EhEdicao = dadosParcela != dadosParcelaValorDefault
  const [habilitarConfirmar, setHabilitarConfirmar] = useState(false)

  const listaCondicionantes = Object.values(CondicionantesEnum)
  const listaIndices = Object.values(IndicesCorrecaoEnum)
  const valorArialLabel = 'change date'
  const minDate = parseISO('1900-01-01 00:00:00')

  const inicializarValidacaoCamposPreenchidos = (): boolean[] => {
    if (EhEdicao) return new Array(9).fill(true)

    const validacaoCamposPreenchidos = new Array(9).fill(false)
    validacaoCamposPreenchidos[CamposParcelaEnum.Colchao] = true
    validacaoCamposPreenchidos[CamposParcelaEnum.TaxaCorrecao] = true
    return validacaoCamposPreenchidos
  }

  const [camposPreenchidos, setCamposPreenchidos] = useState(inicializarValidacaoCamposPreenchidos())

  const dataPorCondicionante = (condicionante: string) => {
    const condicionantes: { [key: string]: Date | null } = {
      [CondicionantesEnum.DataCompra]: dadosDatas.compra,
      [CondicionantesEnum.DataAlvaraAprovacao]: dadosDatas.alvaraAprovacao,
      [CondicionantesEnum.DataAlvaraExecucao]: dadosDatas.alvaraExecucao,
      [CondicionantesEnum.DataRegistroEscritura]: dadosDatas.registroEscritura,
      [CondicionantesEnum.DataPrevisaoRi]: dadosDatas.previsaoRi,
      [CondicionantesEnum.DataPrevisaoLancamento]: dadosDatas.previsaoLancamento,
      [CondicionantesEnum.OutraData]: null
    }
    return condicionantes[condicionante]
  }

  const checkCampoPreenchido = (index: number, novoValor: boolean) => {
    const novoCamposPreenchidos = [...camposPreenchidos]
    novoCamposPreenchidos[index] = novoValor
    setCamposPreenchidos(novoCamposPreenchidos)
    setHabilitarConfirmar(novoCamposPreenchidos.every((item) => item))
  }

  const replaceState = (event: React.ChangeEvent<HTMLInputElement>): Record<string, unknown> => {
    const state = { ...valores } as Record<string, unknown>
    state[event.target.name] = event.target.value
    return state
  }

  const camposHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const state = replaceState(event) as DadosParcela
    setValores(state)
  }

  const condicionanteHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const state = replaceState(event) as DadosParcela
    state.dataBasePrimeiraParcela = dataPorCondicionante(state.condicionante)
    checkCampoPreenchido(CamposParcelaEnum.DataBasePrimeiraParcela, state.condicionante !== CondicionantesEnum.OutraData)
    setValores(state)
  }

  const condicionanteDataBaseCorrecaoHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const state = replaceState(event) as DadosParcela
    state.dataBaseCorrecao = dataPorCondicionante(state.condicionanteDataBaseCorrecao)
    checkCampoPreenchido(CamposParcelaEnum.DataBaseCorrecao, state.condicionanteDataBaseCorrecao !== CondicionantesEnum.OutraData)
    setValores(state)
  }

  const checkCampoPreenchidoPorIndiceCorrecao = (dadosParcela: DadosParcela) => {
    if (dadosParcela.indiceCorrecao === IndicesCorrecaoEnum.SemCorrecao) {
      dadosParcela.condicionanteDataBaseCorrecao = ''
      dadosParcela.dataBaseCorrecao = null
    }

    const novoCamposPreenchidos = [...camposPreenchidos]
    novoCamposPreenchidos[CamposParcelaEnum.TaxaCorrecao] = dadosParcela.indiceCorrecao !== IndicesCorrecaoEnum.OutroIndice

    if (dadosParcela.indiceCorrecao === IndicesCorrecaoEnum.SemCorrecao) {
      novoCamposPreenchidos[CamposParcelaEnum.DataBaseCorrecao] = true
      novoCamposPreenchidos[CamposParcelaEnum.CondicionanteDataBaseCorrecao] = true
    } else if (dadosParcela.condicionanteDataBaseCorrecao === '' && dadosParcela.dataBaseCorrecao === null) {
      novoCamposPreenchidos[CamposParcelaEnum.DataBaseCorrecao] = false
      novoCamposPreenchidos[CamposParcelaEnum.CondicionanteDataBaseCorrecao] = false
    }

    setCamposPreenchidos(novoCamposPreenchidos)
    setHabilitarConfirmar(novoCamposPreenchidos.every((item) => item))
  }

  const indiceCorrecaoHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const state = replaceState(event) as DadosParcela
    checkCampoPreenchidoPorIndiceCorrecao(state)
    setValores(state)
  }

  const dataBasePrimeiraParcelaHandler = (date: Date | null) => {
    const state = { ...valores } as DadosParcela
    state.dataBasePrimeiraParcela = date as Date
    setValores(state)
    validarDataBasePrimeiraParcela(date)
  }

  const dataBaseCorrecaoHandler = (date: Date | null) => {
    const state = { ...valores } as DadosParcela
    state.dataBaseCorrecao = date as Date
    setValores(state)
    validarDataBaseCorrecao(date)
  }

  const btnAdicionarParcelaHandler = () => {
    adicionarParcelaHandler(valores)
  }

  const btnEditarParcelaHandler = () => {
    editarParcelaHandler(valores, indiceEdicao)
  }

  const validarValorParcela = () => {
    const erroValorParcela = mensagensErro.validarInputUsuario(valores.valorParcela.toString())

    checkCampoPreenchido(CamposParcelaEnum.ValorParcela, erroValorParcela === '')

    const validador = new ValidacaoDadosParcela(
      erroValorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarNumeroParcelas = () => {
    const erroNumeroParcelas = mensagensErro.validarInputUsuario(valores.numeroParcelas.toString())

    checkCampoPreenchido(CamposParcelaEnum.NumeroParcelas, erroNumeroParcelas === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      erroNumeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarCondicionante = () => {
    const erroCondicionante = mensagensErro.validarCampoObrigatorio(valores.condicionante.toString())

    checkCampoPreenchido(CamposParcelaEnum.Condicionante, erroCondicionante === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      erroCondicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarDataBasePrimeiraParcelaHandler = () => {
    validarDataBasePrimeiraParcela(null)
  }

  const validarDataBasePrimeiraParcela = (data: Date | null) => {
    const dataBasePrimeiraParcela = data ?? valores.dataBasePrimeiraParcela
    const erroDataBasePrimeiraParcela = mensagensErro.validarData(dataBasePrimeiraParcela)

    checkCampoPreenchido(CamposParcelaEnum.DataBasePrimeiraParcela, erroDataBasePrimeiraParcela === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      erroDataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarColchao = () => {
    checkCampoPreenchido(CamposParcelaEnum.Colchao, true)
  }

  const validarIndiceCorrecao = () => {
    const erroIndiceCorrecao = mensagensErro.validarCampoObrigatorio(valores.indiceCorrecao.toString())

    checkCampoPreenchido(CamposParcelaEnum.IndiceCorrecao, erroIndiceCorrecao === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      erroIndiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarTaxaCorrecao = () => {
    const erroTaxaCorrecao = mensagensErro.validarInputUsuario(valores.taxaCorrecao.toString())

    checkCampoPreenchido(CamposParcelaEnum.TaxaCorrecao, erroTaxaCorrecao === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      erroTaxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarCondicionanteDataBaseCorrecao = () => {
    const erroCondicionanteDataBaseCorrecao = mensagensErro.validarCampoObrigatorio(valores.condicionanteDataBaseCorrecao.toString())

    checkCampoPreenchido(CamposParcelaEnum.CondicionanteDataBaseCorrecao, erroCondicionanteDataBaseCorrecao === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      erroCondicionanteDataBaseCorrecao,
      mensagensErro.dataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  const validarDataBaseCorrecaoHandler = () => {
    validarDataBaseCorrecao(null)
  }

  const validarDataBaseCorrecao = (data?: Date | null) => {
    const dataBaseCorrecao = data ?? valores.dataBaseCorrecao
    const erroDataBaseCorrecao = mensagensErro.validarData(dataBaseCorrecao)

    checkCampoPreenchido(CamposParcelaEnum.DataBaseCorrecao, erroDataBaseCorrecao === '')

    const validador = new ValidacaoDadosParcela(
      mensagensErro.valorParcela,
      mensagensErro.numeroParcelas,
      mensagensErro.condicionante,
      mensagensErro.dataBasePrimeiraParcela,
      mensagensErro.indiceCorrecao,
      mensagensErro.taxaCorrecao,
      mensagensErro.condicionanteDataBaseCorrecao,
      erroDataBaseCorrecao
    )

    setMensagensErro(validador)
  }

  return (
    <S.Wrapper data-cy="cardNovaParcela">
      <DialogHeader titulo={EhEdicao ? 'EDITAR PARCELA' : 'NOVA PARCELA'} closedDialog={closeDialogParcelaHandler} />
      <Grid orientacao="row" gridTemplateColumns="repeat(4,1fr)" gridTemplateRow="1fr" gridGap="32px" margin="0">
        <div>
          <S.Label>Valor da Parcela (R$)</S.Label>
          <TextField
            placeholder="000.000,00"
            type="text"
            name="valorParcela"
            data-cy="campoValorParcela"
            variant="outlined"
            size="small"
            InputProps={{
              inputComponent: MascaraDecimal as unknown as ElementType<InputBaseComponentProps>
            }}
            value={valores.valorParcela}
            onChange={camposHandler}
            onBlur={validarValorParcela}
            error={mensagensErro.valorParcela != ''}
            helperText={mensagensErro.valorParcela}
          />
        </div>
        <div>
          <S.Label>Número de Parcelas</S.Label>
          <TextField
            placeholder="000"
            type="text"
            name="numeroParcelas"
            data-cy="campoNumeroParcelas"
            variant="outlined"
            size="small"
            InputProps={{
              inputComponent: MascaraInteiro as unknown as ElementType<InputBaseComponentProps>
            }}
            value={valores.numeroParcelas}
            onChange={camposHandler}
            onBlur={validarNumeroParcelas}
            error={mensagensErro.numeroParcelas != ''}
            helperText={mensagensErro.numeroParcelas}
          />
        </div>
        <div>
          <S.Label>Condicionante</S.Label>
          <TextField
            name="condicionante"
            data-cy="campoCondicionante"
            fullWidth
            variant="outlined"
            size="small"
            select
            SelectProps={{
              displayEmpty: true
            }}
            value={valores.condicionante}
            onChange={condicionanteHandler}
            onBlur={validarCondicionante}
            error={mensagensErro.condicionante != ''}
            helperText={mensagensErro.condicionante}
          >
            <MenuItem value="" disabled>
              Selecione
            </MenuItem>
            {listaCondicionantes.map((condicionante, index) => (
              <MenuItem key={index} data-cy={`condicionante_${index}`} value={condicionante}>
                {condicionante}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <div>
          <S.Label>Data Base da 1ª Parcela</S.Label>
          <KeyboardDatePicker
            placeholder="00/00/0000"
            inputVariant="outlined"
            margin="none"
            name="dataBasePrimeiraParcela"
            data-cy="campoDataBasePrimeiraParcela"
            format="dd/MM/yyyy"
            minDate={minDate}
            openTo="date"
            size="small"
            views={['year', 'month', 'date']}
            disabled={valores.condicionante !== CondicionantesEnum.OutraData}
            KeyboardButtonProps={{
              'aria-label': valorArialLabel
            }}
            value={valores.dataBasePrimeiraParcela}
            onChange={dataBasePrimeiraParcelaHandler}
            onBlur={validarDataBasePrimeiraParcelaHandler}
            error={mensagensErro.dataBasePrimeiraParcela != ''}
            helperText={mensagensErro.dataBasePrimeiraParcela}
            keyboardIcon={<Event size={24} color={theme.colors.gray4} />}
          />
        </div>
        <div>
          <S.Label>Colchão - 1ª Parcela (Dias)</S.Label>
          <TextField
            placeholder="000"
            type="text"
            name="colchaoPrimeiraParcela"
            data-cy="campoColchaoPrimeiraParcela"
            variant="outlined"
            size="small"
            InputProps={{
              inputComponent: MascaraInteiro as unknown as ElementType<InputBaseComponentProps>
            }}
            value={valores.colchaoPrimeiraParcela}
            onChange={camposHandler}
            onBlur={validarColchao}
            helperText={campoOpcional}
          />
        </div>
        <div>
          <S.Label>Índice de Correção</S.Label>
          <TextField
            name="indiceCorrecao"
            data-cy="campoIndiceCorrecao"
            fullWidth
            variant="outlined"
            size="small"
            select
            SelectProps={{
              displayEmpty: true
            }}
            value={valores.indiceCorrecao}
            onChange={indiceCorrecaoHandler}
            onBlur={validarIndiceCorrecao}
            error={mensagensErro.indiceCorrecao != ''}
            helperText={mensagensErro.indiceCorrecao}
          >
            <MenuItem value="" disabled>
              Selecione
            </MenuItem>
            {listaIndices.map((indice, index) => (
              <MenuItem key={index} data-cy={`indice_${index}`} value={indice}>
                {indice}
              </MenuItem>
            ))}
          </TextField>
        </div>
        {valores.indiceCorrecao === IndicesCorrecaoEnum.OutroIndice && (
          <>
            <div>
              <S.Label>Taxa de Correção (%)</S.Label>
              <TextField
                placeholder="0,00"
                type="text"
                name="taxaCorrecao"
                data-cy="campoTaxaCorecao"
                variant="outlined"
                size="small"
                InputProps={{
                  inputComponent: MascaraDecimal as unknown as ElementType<InputBaseComponentProps>
                }}
                value={valores.taxaCorrecao}
                onChange={camposHandler}
                onBlur={validarTaxaCorrecao}
                error={mensagensErro.taxaCorrecao != ''}
                helperText={mensagensErro.taxaCorrecao}
              />
            </div>
            <div></div>
          </>
        )}

        {valores.indiceCorrecao !== IndicesCorrecaoEnum.SemCorrecao && (
          <>
            <div>
              <S.Label>Cond. Data Base Correção</S.Label>
              <TextField
                name="condicionanteDataBaseCorrecao"
                data-cy="campoCondicionanteDataBaseCorrecao"
                fullWidth
                variant="outlined"
                size="small"
                select
                SelectProps={{
                  displayEmpty: true
                }}
                value={valores.condicionanteDataBaseCorrecao}
                onChange={condicionanteDataBaseCorrecaoHandler}
                onBlur={validarCondicionanteDataBaseCorrecao}
                error={mensagensErro.condicionanteDataBaseCorrecao != ''}
                helperText={mensagensErro.condicionanteDataBaseCorrecao}
              >
                <MenuItem value="" disabled>
                  Selecione
                </MenuItem>
                {listaCondicionantes.map((condicionante, index) => (
                  <MenuItem key={index} data-cy={`condicionanteDataBaseCorrecao_${index}`} value={condicionante}>
                    {condicionante}
                  </MenuItem>
                ))}
              </TextField>
            </div>
            <div>
              <S.Label>Data Base Correção</S.Label>
              <KeyboardDatePicker
                placeholder="00/00/0000"
                inputVariant="outlined"
                margin="none"
                name="dataBaseCorrecao"
                data-cy="campoDataBaseCorrecao"
                format="dd/MM/yyyy"
                minDate={minDate}
                autoOk={true}
                openTo="date"
                size="small"
                views={['year', 'month', 'date']}
                KeyboardButtonProps={{
                  'aria-label': valorArialLabel
                }}
                disabled={valores.condicionanteDataBaseCorrecao !== CondicionantesEnum.OutraData}
                value={valores.dataBaseCorrecao}
                onChange={dataBaseCorrecaoHandler}
                onBlur={validarDataBaseCorrecaoHandler}
                error={mensagensErro.dataBaseCorrecao != ''}
                helperText={mensagensErro.dataBaseCorrecao}
                keyboardIcon={<Event size={24} color={theme.colors.gray4} />}
              />
            </div>
          </>
        )}
      </Grid>
      <S.GridBotoes orientacao="column" gridTemplateColumns="repeat(2,20%)" gridTemplateRow="1fr" gridGap="20px" margin="0">
        <div>
          <Button
            data-cy="btnCancelarParcela"
            onClick={closeDialogParcelaHandler}
            variant="text"
            style={{ height: '40px', width: '136px', fontSize: '14px' }}
            color="primary"
          >
            Cancelar
          </Button>
        </div>
        <div>
          <Button
            data-cy="btnAdicionarParcela"
            variant="contained"
            style={{ height: '40px', fontSize: '14px' }}
            onClick={EhEdicao ? btnEditarParcelaHandler : btnAdicionarParcelaHandler}
            color="secondary"
            disabled={!habilitarConfirmar}
          >
            {EhEdicao ? 'Salvar Alterações' : 'Adicionar Parcela'}
          </Button>
        </div>
      </S.GridBotoes>
    </S.Wrapper>
  )
}

export default NovaParcela
